import ArchiveSubjectType from '@Application/SubjectTypeDefinitions/Commands/ArchiveSubjectType';
import archiveSubjectType from '@Application/SubjectTypeDefinitions/archiveSubjectType';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type SubjectTypeDefinitionView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionView';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { ConfirmDialog } from '@verclam/ui-kit';

type Props = {
	readonly subjectType: SubjectTypeDefinitionView;
	readonly visible: boolean;
	readonly onHide: () => void;
	/** Called once the type is archived, e.g. by a screen that still displays it. */
	readonly onArchived?: () => void;
};

/** Archiving is reversible and keeps the dossiers of the type, so a plain `ConfirmDialog` is enough. */
const ArchiveSubjectTypeDialog: React.FC<Props> = ({ subjectType, visible, onHide, onArchived }) => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const handleConfirm = React.useCallback(() => {
		if (accountId !== null) {
			dispatch(archiveSubjectType(new ArchiveSubjectType(accountId, subjectType.id)))
				.then(() => onArchived?.())
				.catch(ignoreHandledError);
		}
		onHide();
	}, [accountId, dispatch, subjectType.id, onHide, onArchived]);

	return (
		<ConfirmDialog
			blockScroll
			closable
			draggable={false}
			visible={visible}
			onHide={onHide}
			header="Archiver le type"
			message={`Archiver le type « ${subjectType.wording.plural} » ? Il disparaîtra des menus et on ne pourra plus créer de dossier de ce type. Ses dossiers sont conservés : vous pourrez le restaurer.`}
			acceptSeverity="danger"
			acceptLabel="Archiver"
			rejectLabel="Annuler"
			accept={handleConfirm}
			reject={onHide}
		/>
	);
};

export default ArchiveSubjectTypeDialog;
